/**
 * ThoughtLeadershipFeaturePage — /features/thought-leadership
 *
 * Explains the LinkedIn Executive Thought Leadership plan. Uses hypothetical
 * post cards + a "how a post is generated" visual walkthrough so cold
 * visitors can see the product shape and understand the choices behind
 * every post — building trust before they buy.
 */
import React from 'react';
import { AppShell } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Rocket, Sparkles, CheckCircle2, ArrowRight, ChevronRight, Target,
  MessageSquare, TrendingUp, Layers, RefreshCw, Wand2, ShieldCheck, ThumbsUp,
} from 'lucide-react';

const HYPOTHETICAL_POSTS = [
  {
    angle: 'CONTRARIAN TAKE',
    tone: 'Balanced',
    hook: 'Every product leader I know is obsessed with roadmap velocity.',
    body:
      "Not one of them can tell me the last three assumptions they killed on purpose.\n\n" +
      "The teams that shipped less last quarter are outperforming the ones that shipped more. Because \"less\" was a decision — not a stall.\n\n" +
      "Three questions I now start every planning cycle with:\n" +
      "  → What are we shipping only because we already promised it?\n" +
      "  → What would we cut if this quarter had 30% fewer engineers?\n" +
      "  → What's the one bet we'd double if next quarter had 30% more?",
    cta: 'Which one hit hardest for you?',
    metrics: { reactions: 384, comments: 62, shares: 41 },
  },
  {
    angle: 'PERSONAL STORY',
    tone: 'Storytelling',
    hook: 'I got fired from my first PM job on day 46.',
    body:
      "The founder walked in Monday morning, closed the door, and said: \"You have great instincts and you can't answer a single hard question.\"\n\n" +
      "He was right. I'd spent 45 days optimising the roadmap and zero days learning why revenue was flat.\n\n" +
      "The thing I understood the day I walked out: judgment isn't taught by mentors. It's forged by the exact conversations we avoid.",
    cta: 'What was your day 46 lesson?',
    metrics: { reactions: 812, comments: 148, shares: 92 },
  },
  {
    angle: 'FRAMEWORK',
    tone: 'Strict',
    hook: 'The pricing reset that lifted our net retention past 120% wasn\'t a discount.',
    body:
      "It was a 4-step audit any B2B founder can run this week:\n\n" +
      "1. Interview your last 5 renewals AND your last 5 churns.\n" +
      "2. Score each account on the value they actually consumed (not the value they signed for).\n" +
      "3. Regroup accounts into 3 tiers by consumption, not company size.\n" +
      "4. Reset prices per tier — not per feature list.\n\n" +
      "That reset moved us from a 92% GRR to a 120%+ NRR in two quarters. No new sales headcount.",
    cta: 'Which step are you skipping today?',
    metrics: { reactions: 1274, comments: 217, shares: 186 },
  },
];

function PostCard({ post, idx }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-40px_rgba(15,23,42,0.5)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
            {post.angle}
          </span>
          <span className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">
            Tone · {post.tone}
          </span>
        </div>
        <span className="text-[10px] text-slate-400 font-mono-edge">Post {idx + 1}</span>
      </div>
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-sky-500 to-blue-700 text-white flex items-center justify-center text-[12px] font-bold">
            AR
          </div>
          <div>
            <p className="text-[13px] font-semibold text-slate-900 leading-tight">Ananya Rao</p>
            <p className="text-[11px] text-slate-500">Head of Product · Fintech · 2h</p>
          </div>
        </div>
        <p className="text-[14px] font-semibold text-slate-900 leading-snug">{post.hook}</p>
        <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-line">{post.body}</p>
        <p className="mt-3 text-[13px] text-slate-800 italic">{post.cta}</p>
        <div className="mt-4 flex items-center gap-5 pt-3 border-t border-slate-100 text-[11.5px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><ThumbsUp size={12} /> {post.metrics.reactions.toLocaleString()}</span>
          <span>{post.metrics.comments} comments</span>
          <span>{post.metrics.shares} reposts</span>
        </div>
      </div>
    </div>
  );
}

function GenerationWalkthrough() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] overflow-hidden">
      <div className="flex items-center gap-1.5 h-8 border-b border-slate-100 bg-slate-50 px-3">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[11px] text-slate-500 font-mono-edge tracking-wide">blayzrr.com / dashboard / posts / new</span>
      </div>
      <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">1 · Post angle</p>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5">
              {['Contrarian take', 'Personal story', 'Framework', 'Prediction'].map((a, i) => (
                <div key={a} className={`text-[12px] rounded-lg border px-2.5 py-2 ${i === 2 ? 'border-sky-500 bg-sky-50 text-slate-900 font-semibold' : 'border-slate-200 text-slate-600'}`}>
                  {a}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">2 · Voice tone</p>
            <div className="mt-1.5 flex items-center gap-2">
              {['Strict', 'Balanced', 'Storytelling'].map((t) => (
                <div key={t} className={`text-[11.5px] rounded-full px-3 py-1 border ${t === 'Balanced' ? 'border-sky-500 bg-sky-50 text-slate-900 font-semibold' : 'border-slate-200 text-slate-600'}`}>
                  {t}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">3 · Anchor from your profile</p>
            <div className="mt-1.5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-700 leading-snug">
              "Rebuilt onboarding from a 4-person team into a 22-person growth engine at Acme Payments (2021-2023)." <span className="text-slate-400">— from your Experience section</span>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">4 · Hook variants (pick one)</p>
            <div className="mt-1.5 space-y-1.5">
              {[
                'The onboarding rebuild every founder underestimates.',
                'How I turned a 4-person team into a 22-person growth engine.',
                'Onboarding isn\'t a support function. Here\'s the proof.',
              ].map((h, i) => (
                <div key={h} className={`text-[12px] rounded-lg border px-2.5 py-2 leading-snug ${i === 1 ? 'border-emerald-500 bg-emerald-50/60 text-slate-900 font-semibold' : 'border-slate-200 text-slate-600'}`}>
                  {h}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">5 · Guardrails</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5"><ShieldCheck size={10} /> No invented numbers</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5"><Target size={10} /> Anchored to profile</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5"><MessageSquare size={10} /> Post ≤ 1300 chars</span>
            </div>
          </div>
          <button
            type="button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 text-[13px] font-bold h-10"
          >
            <Wand2 size={14} /> Generate post
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ThoughtLeadershipFeaturePage() {
  const navigate = useNavigate();
  return (
    <AppShell>
      <section className="relative overflow-x-clip bg-gradient-to-b from-amber-50/60 via-white to-white dark:from-amber-950/10 dark:via-slate-950 dark:to-slate-900 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-14 lg:pt-16 pb-10 sm:pb-14 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 px-3 py-1 text-[11.5px] font-mono-edge tracking-[0.16em] uppercase">
              <Rocket size={12} /> LinkedIn Executive Thought Leadership
            </div>
            <h1 className="mt-4 text-[34px] sm:text-[42px] lg:text-[48px] font-semibold tracking-[-0.02em] leading-[1.05] text-foreground" data-testid="thought-leadership-heading">
              A month of posts in your voice — with the strategy behind every line.
            </h1>
            <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed max-w-[560px]">
              8-12 ready-to-publish LinkedIn posts every month, written from the outcomes and stories that already live in your profile. Pick an angle, a tone, an anchor from your experience — see the post drafted end-to-end before it goes anywhere near your audience.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => navigate('/pricing')} data-testid="thought-leadership-cta-primary" className="gap-2">
                <Sparkles size={16} /> See the plan <ArrowRight size={16} />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/features/profile-success')} data-testid="thought-leadership-cta-profile">
                Start with the profile audit
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-[12.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> 8-12 posts / month</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> 4 carousels / month</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> Cancel anytime</span>
            </div>
          </div>
          <div className="relative">
            <div className="grid gap-3">
              <div className="ml-6 sm:ml-10">
                <PostCard post={HYPOTHETICAL_POSTS[0]} idx={0} />
              </div>
              <div className="mr-6 sm:mr-10">
                <PostCard post={HYPOTHETICAL_POSTS[1]} idx={1} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How a post is generated */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="max-w-3xl">
          <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-amber-600">How a post is generated</p>
          <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight text-foreground">
            Every post starts from a fact in your profile — never a blank page.
          </h2>
          <p className="mt-3 text-[14.5px] text-muted-foreground leading-relaxed">
            Pick an angle. Pick a tone. Choose an anchor from your own Experience or Featured content. Blayzrr drafts three hook variants and stitches together a full post — with guardrails so nothing gets invented on your behalf.
          </p>
        </div>
        <div className="mt-8">
          <GenerationWalkthrough />
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Layers, title: 'Multiple angles', body: 'Contrarian, personal story, framework, prediction — every post is deliberately positioned, not generic.' },
            { icon: RefreshCw, title: 'Regenerate on demand', body: 'Not feeling a hook? One-click regen against a different tone or angle — full audit is not re-run.' },
            { icon: TrendingUp, title: 'Track what lands', body: 'Post-level insights on which angle + tone combo is pulling for your audience so the next month writes smarter.' },
          ].map((s) => (
            <div key={s.title} className="rounded-2xl border border-border bg-background p-5">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                <s.icon size={18} />
              </div>
              <p className="mt-4 text-[15px] font-semibold text-foreground">{s.title}</p>
              <p className="mt-1.5 text-[13.5px] text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sample post gallery */}
      <section className="border-t border-border bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="max-w-3xl mb-8">
            <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-amber-600">Sample posts</p>
            <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight text-foreground">
              Three angles, three tones — same profile, three different rooms it lands in.
            </h2>
            <p className="mt-3 text-[14.5px] text-muted-foreground leading-relaxed">
              These are illustrative posts on a hypothetical profile. Yours are always built from what your Experience and Featured sections can actually back up.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {HYPOTHETICAL_POSTS.map((p, i) => (
              <PostCard key={p.angle} post={p} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="rounded-2xl border border-border bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/20 dark:to-slate-900 p-6 sm:p-10 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-amber-600">Ready to be seen</p>
            <h2 className="mt-2 text-[24px] sm:text-[28px] font-semibold tracking-tight text-foreground leading-tight">
              Start with the audit. Turn it into a month of posts that build authority.
            </h2>
            <p className="mt-2 text-[13.5px] text-muted-foreground max-w-[560px]">
              Sharpen your profile first with LinkedIn Profile Success, then let Executive Thought Leadership publish in your voice while you keep shipping the real work.
            </p>
          </div>
          <div className="flex flex-col gap-2 min-w-[220px]">
            <Button size="lg" className="gap-2" onClick={() => navigate('/pricing')} data-testid="thought-leadership-cta-plan">
              See pricing <ChevronRight size={16} />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/')} data-testid="thought-leadership-cta-home">
              Back to home
            </Button>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
